import { useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import { RPC_URLS, CONTRACTS } from '../utils/contracts';

// PulseX v2 Pair ABI (simplified for price fetching)
const PAIR_ABI = [
  "function getReserves() external view returns (uint112 reserve0, uint112 reserve1, uint32 blockTimestampLast)",
  "function token0() external view returns (address)"
];

// Price of 1 `baseToken` in the paired token, from pool reserves. All tokens involved
// here (JDAI, WPLS, bridged DAI) use 18 decimals, so no per-token decimals handling.
const pairPrice = async (provider, pairAddress, baseToken) => {
  const pair = new ethers.Contract(pairAddress, PAIR_ABI, provider);
  const [[reserve0, reserve1], token0] = await Promise.all([
    pair.getReserves(),
    pair.token0()
  ]);
  const baseIs0 = token0.toLowerCase() === baseToken.toLowerCase();
  const base = Number(ethers.formatEther(baseIs0 ? reserve0 : reserve1));
  const quote = Number(ethers.formatEther(baseIs0 ? reserve1 : reserve0));
  return base > 0 ? quote / base : 0;
};

/**
 * Market prices from PulseX pools, fully independent of the system oracle:
 *  - jdaiPriceInPls:  JDAI/WPLS pool ratio (PLS per JDAI)
 *  - marketPlsPrice:  WPLS/DAI pool price (USD per PLS)
 *  - jdaiMarketPrice: product of the two (USD per JDAI) — matches what DexScreener shows
 */
export const usePulseXPrice = () => {
  const [prices, setPrices] = useState({
    jdaiPriceInPls: 0,
    marketPlsPrice: 0,
    jdaiMarketPrice: 0
  });
  const [loading, setLoading] = useState(true); // Start with loading true
  const [error, setError] = useState(null);

  const fetchPrices = useCallback(async () => {
    try {
      setError(null);

      // Resilient read provider: query multiple PulseChain RPCs so a single
      // slow or down endpoint doesn't break price fetches.
      const provider = new ethers.FallbackProvider(
        RPC_URLS.map((url) => new ethers.JsonRpcProvider(url))
      );

      const [jdaiPriceInPls, marketPlsPrice] = await Promise.all([
        pairPrice(provider, CONTRACTS.JDAI_PLS_PAIR, CONTRACTS.JDAI),
        pairPrice(provider, CONTRACTS.WPLS_DAI_PAIR, CONTRACTS.PLS)
      ]);

      setPrices({
        jdaiPriceInPls,
        marketPlsPrice,
        jdaiMarketPrice: jdaiPriceInPls * marketPlsPrice
      });
    } catch (err) {
      console.error('Error fetching PulseX prices:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPrices();

    // Update every 30 seconds
    const interval = setInterval(fetchPrices, 30000);

    // Timeout to ensure loading doesn't stay true forever
    const timeout = setTimeout(() => {
      setLoading(false);
    }, 10000); // 10 second max wait

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [fetchPrices]);

  return {
    ...prices,
    loading,
    error,
    refresh: fetchPrices
  };
};
